import { inject, Injectable } from '@angular/core';
import { UserIdCode } from './models/user-id-code';
import { environment } from '../../../environments/environment';
import { ComoApiService } from './como-api.service';
import { ComoTransaction } from './models/como-transaction';
import { ComoTransactionOperations } from './enums/como-transaction-operations';
import { BehaviorSubject, withLatestFrom } from 'rxjs';
import { ComoReward } from './models/como-reward';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { UserStateService } from '../../state/user/user-state';

@Injectable({
	providedIn: 'root',
})
export class ComoStateService {
	readonly MemberDetailsDeff = {
		//todo: create enum
		pointsBalance: {
			balance: {
				monetary: 0,
				nonMonetary: 0,
			},
		},
		genericWallet1Balance: {
			balance: {
				monetary: 0,
				nonMonetary: 0,
			},
		},
		creditBalance: {
			balance: {
				monetary: 0,
			},
		},
		collectedPoints: 0,
		email: '',
		userUid: '',
	};

	points$ = new BehaviorSubject(0);
	memberDetails$ = new BehaviorSubject(this.MemberDetailsDeff);
	userIdCode$: BehaviorSubject<UserIdCode> = new BehaviorSubject(null);
	gainedTransactions$: BehaviorSubject<ComoTransaction[]> = new BehaviorSubject([]);
	redeemedTransaction$: BehaviorSubject<ComoTransaction[]> = new BehaviorSubject([]);
	rewards$: BehaviorSubject<{ en: ComoReward[]; ar: ComoReward[] }> = new BehaviorSubject({ en: [], ar: [] });
	pointRewards$: BehaviorSubject<{ en: ComoReward[]; ar: ComoReward[] }> = new BehaviorSubject({ en: [], ar: [] });
	deals$ = new BehaviorSubject([]);

	checkComoStateStatus() {
		if (!this.memberDetails$.value?.email && this.userStateSvc.customer) {
			this.snackBarCtrl.open(this.translateService.instant('axios_errors.como_error'), null, { duration: 3000 });
		}
	}

	private comoApiSvc = inject(ComoApiService);
	private userStateSvc = inject(UserStateService);
	private snackBarCtrl = inject(MatSnackBar);
	private translateService = inject(TranslateService);

	constructor() {
		this.userStateSvc.customerAuth$.pipe(withLatestFrom(this.memberDetails$)).subscribe(async ([res, member]) => {
			if (res?.customer?.email && (!member?.userUid || res.customer?.uid !== member?.userUid)) this.initUserComoState();
		});
	}

	async initUserComoState() {
		this.clearState();

		try {
			await this.initMemberDetails();
			this.userIdCode$.next(await this.comoApiSvc.userId(environment.masterVenue, this.userStateSvc.customer.email));
			await this.initRewards();
			await this.initPointRewards();
			await this.initTransactions();
			this.deals$.next((await this.comoApiSvc.deals(environment.masterVenue)).deals);
		} catch (error) {
			console.error(error);
		}
	}

	async initTransactions() {
		const res = await this.comoApiSvc.transactions(environment.masterVenue, this.userStateSvc.customer.email);
		this.redeemedTransaction$.next([]);
		this.gainedTransactions$.next([]);
		res.transactions.forEach((t: ComoTransaction) => {
			if (t.operation === ComoTransactionOperations.AddPoints)
				this.gainedTransactions$.next([...this.gainedTransactions$.value, t]);
			else this.redeemedTransaction$.next([...this.redeemedTransaction$.value, t]);
		});
	}

	async initMemberDetails() {
		const memberDetails = await this.comoApiSvc.memberDetails(
			environment.masterVenue,
			this.userStateSvc.customer.email
		);
		memberDetails.userUid = this.userStateSvc.customer.uid;
		this.memberDetails$.next(memberDetails);
		this.points$.next(this.memberDetails$.value.genericWallet1Balance.balance.monetary / 100);
	}

	async initRewards() {
		const rewards = (await this.comoApiSvc.gifts(environment.masterVenue, this.userStateSvc.customer.email)).gifts;
		this.rewards$.next(this.sortRewards(rewards));
	}

	async initPointRewards() {
		const rewards = (await this.comoApiSvc.pointShop(environment.masterVenue)).items;
		this.pointRewards$.next(this.sortRewards(rewards)); //todo: add ps_ for id
	}

	sortRewards(rewards: ComoReward[]) {
		const sortedRewards = { en: [], ar: [] };

		return rewards.reduce((rewards, r) => {
			if (r.tags?.includes('EN'))
				rewards.en.push(r); // temporary
			else if (r.tags?.includes('AR')) rewards.ar.push(r);
			else if (r.name.includes('EN_')) {
				r.name = r.name.slice(3);
				rewards.en.push(r);
			} else if (r.name.includes('AR_')) {
				r.name = r.name.slice(3);
				rewards.ar.push(r);
			} else {
				//todo: remove, only for testing
				rewards.en.push(r);
				rewards.ar.push(r);
			}
			return rewards;
		}, sortedRewards);
	}

	clearState() {
		this.points$.next(0);
		this.memberDetails$.next(this.MemberDetailsDeff);
		this.userIdCode$.next(null);
		this.redeemedTransaction$.next(null);
		this.gainedTransactions$.next(null);
		this.rewards$.next(null);
	}
}
